import React, { useState } from 'react';
import { Autocomplete, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
    menuPropertyPrivate,
    getProfileMenu,
    getOwnerPortalMenu,
    getZinantisMenu,
    getZitoneoMenu,
} from '../../../utils/menusData';
import { useHistory } from 'react-router-dom';
import useTranslate from '../../../customHooks/useTranslate';
import { useAppConfigurationSections } from '../../../contexts/AppConfigurationContext';
import { AppSectionsProfile } from '../../../types/appConfiguration/appConfigTypes';
import useUserProfile from '../../../customHooks/useUserProfile';
import {
    CoreMenuItem,
    MenuItemLink,
    ProfileMenuItem,
    ZinantisMenuItem,
    ZitoneoMenuItem,
} from '../../../types/menus/menusTypes';

interface Option {
    label: string;
    link: string;
    target?: string;
    section: string;
}

function HighlightedOption({
    label,
    inputValue,
}: {
    label: string;
    inputValue: string;
}) {
    if (!inputValue) return <span>{label}</span>;

    const regex = new RegExp(`(${inputValue})`, 'gi');
    const parts = label.split(regex);

    return (
        <>
            {parts.map((part, index) =>
                part.toLowerCase() === inputValue.toLowerCase() ? (
                    <span
                        key={index}
                        style={{ fontWeight: 700, color: 'var(--primary-400)' }}
                    >
                        {part}
                    </span>
                ) : (
                    <span key={index}>{part}</span>
                )
            )}
        </>
    );
}

const isMenuItemLink = (
    item: ProfileMenuItem
): item is MenuItemLink<AppSectionsProfile> => 'link' in item;

function buildOptions({
    zinantisMenuItems,
    profileMenuItems,
    coreMenuItems,
    zitoneoMenuItems,
    translate,
}: {
    zinantisMenuItems: ZinantisMenuItem[];
    profileMenuItems: ProfileMenuItem[];
    coreMenuItems: CoreMenuItem[];
    zitoneoMenuItems: ZitoneoMenuItem[];
    translate: (key: string) => string;
}): Option[] {
    const opts: Option[] = [];

    opts.push(
        ...coreMenuItems.map(item => ({
            label: translate(item.text),
            link: item.link,
            section: translate('dashboard'),
        }))
    );

    opts.push(
        ...profileMenuItems.filter(isMenuItemLink).map(item => ({
            label: translate(item.text),
            link: item.link,
            section: translate('profile'),
        }))
    );

    opts.push(
        ...menuPropertyPrivate
            .filter(item => item.name !== 'home')
            .map(item => ({
                label: translate(item.name),
                link: item.linkTo,
                section: translate('support_center'),
            }))
    );

    opts.push(
        ...zinantisMenuItems.map(item => ({
            label: translate(item.text),
            link: item.link,
            section: 'Zinantis',
        }))
    );
    opts.push(
        ...zitoneoMenuItems.map(item => ({
            label: translate(item.text),
            link: item.link,
            section: 'Zitoneo',
            target: item.target,
        }))
    );

    return opts;
}

function sortOptionsBySection(
    options: Option[],
    translate: (key: string) => string
): Option[] {
    const sectionOrder: Record<string, number> = {
        [translate('support_center')]: 0,
        [translate('dashboard')]: 1,
        [translate('Zinantis')]: 2,
        [translate('Zitoneo')]: 3,
        [translate('profile')]: 4,
    };

    return [...options].sort(
        (a, b) =>
            (sectionOrder[a.section] ?? 99) - (sectionOrder[b.section] ?? 99)
    );
}

export default function TypeaheadSearchbar() {
    const translate = useTranslate();
    const history = useHistory();
    const [value, setValue] = useState('');
    const {
        zinantis,
        core,
        profile,
        isCoreSectionVisible,
        isZinantisSectionVisible,
        isProfileSectionVisible,
        zitoneo,
        isZitoneoSectionVisible,
    } = useAppConfigurationSections();
    const {
        settings: { adminUser },
    } = useUserProfile();

    const ownerPortalMenu = getOwnerPortalMenu(
        isCoreSectionVisible,
        core.pages
    );

    const zinantisMenu = getZinantisMenu(
        isZinantisSectionVisible,
        adminUser,
        zinantis.pages
    );

    const zitoneoMenu = getZitoneoMenu(
        isZitoneoSectionVisible,
        adminUser,
        zitoneo.pages
    );

    let userMenuOptions = getProfileMenu(
        isProfileSectionVisible,
        profile.pages
    );

    userMenuOptions = userMenuOptions = userMenuOptions
        .filter(isMenuItemLink)
        .filter(item => item.onClick === undefined);

    let options = buildOptions({
        zinantisMenuItems: zinantisMenu,
        profileMenuItems: userMenuOptions,
        coreMenuItems: ownerPortalMenu,
        zitoneoMenuItems: zitoneoMenu,
        translate,
    });
    options = sortOptionsBySection(options, translate);

    return (
        <Autocomplete
            freeSolo
            value={null}
            inputValue={value}
            options={options}
            groupBy={option => option.section}
            filterOptions={(filterOptions, { inputValue }) => {
                if (!inputValue) return options;
                const lowerInput = inputValue.toLowerCase();
                return filterOptions.filter(
                    option =>
                        option.label.toLowerCase().includes(lowerInput) ||
                        option.section.toLowerCase().includes(lowerInput)
                );
            }}
            onChange={(e, selectedValue) => {
                if (selectedValue && typeof selectedValue !== 'string') {
                    if (selectedValue.target) {
                        window.open(
                            selectedValue.link,
                            selectedValue.target,
                            'noopener,noreferrer'
                        );
                    } else {
                        history.push(selectedValue.link);
                    }
                }
                setValue('');
            }}
            onClose={() => setValue('')}
            sx={{
                width: '300px',
                '& .MuiOutlinedInput-root': {
                    padding: '5px 10px !important',
                },
            }}
            renderInput={params => (
                <TextField
                    {...params}
                    placeholder={`${translate('search')}...`}
                    variant='outlined'
                    sx={{
                        '& .MuiAutocomplete-input': {
                            padding: '0 !important',
                        },
                        '& input::placeholder': {
                            textTransform: 'capitalize',
                        },
                    }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <InputAdornment position='end'>
                                <SearchIcon color='action' />
                            </InputAdornment>
                        ),
                    }}
                    value={value}
                    onChange={e => setValue(e.target.value)}
                />
            )}
            renderOption={(props, option) => (
                <li {...props}>
                    <HighlightedOption
                        label={option.label}
                        inputValue={value}
                    />
                </li>
            )}
            renderGroup={params => (
                <li key={params.key}>
                    <div
                        style={{
                            fontWeight: 700,
                            fontSize: '0.8rem',
                            textTransform: 'uppercase',
                            padding: '8px 12px 4px',
                            borderBottom: '1px solid var(--border)',
                        }}
                    >
                        {params.group}
                    </div>
                    <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                </li>
            )}
        />
    );
}
